import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import PricingSelector from '../components/common/PricingSelector';
import AvailabilityCalendar from '../components/common/AvailabilityCalendar';
import DeliverySelector from '../components/common/DeliverySelector';
import ZoneChecker from '../components/common/ZoneChecker';
import UrgencyBadge from '../components/common/UrgencyBadge';
import TrustBlock from '../components/common/TrustBlock';
import ReviewsWidget from '../components/common/ReviewsWidget';
import CallButton from '../components/common/CallButton';
import MobileStickyBar from '../components/common/MobileStickyBar';
import BookingInfoBlock from '../components/common/BookingInfoBlock';
import PriceBreakdown from '../components/common/PriceBreakdown';
import PhotoboothSelector from '../components/common/PhotoboothSelector';
import SEO from '../components/common/SEO';
import { getProductSEO, SITE } from '../utils/seoUtils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PROFILE_DISCOUNTS, getDiscountInfo } from '../config/discounts';
import { calculateFullPrice, applyWeekdayDiscount, BOOKING_TYPES } from '../config/pricingRules';
import { toast } from 'sonner';
import ContactModal from '../components/common/ContactModal';
import {
  ShoppingBag, ArrowLeft, Clock, FileText,
  ChevronDown, ChevronUp, Info, Tag, Percent, MessageCircle, Check, AlertCircle, Truck, CalendarDays, Star
} from 'lucide-react';

// ── Mapping nb jours → clé durée ────────────────────────────────────────
function daysToKey(nb) {
  if (nb <= 1) return '1j';
  if (nb === 2) return '2j';
  if (nb === 3) return '3j';
  if (nb === 4) return '4j';
  if (nb === 5) return '5j';
  if (nb <= 7) return 'semaine';
  return '10j';
}

function formatDate(input) {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input.length === 10 ? input + 'T12:00:00' : input) : input;
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${d.getFullYear()}`;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DURATION_LABELS = {
  demi_journee: 'Demi-journée',
  '1j': '1 jour',
  '2j': '2 jours',
  '3j': '3 jours',
  '4j': '4 jours',
  '5j': '5 jours',
  semaine: '1 semaine (-15%)',
  '10j': '10 jours',
};

export default function ProductPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [selectedDuration, setSelectedDuration] = useState('1j');
  const [contactOpen, setContactOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [deliveryResult, setDeliveryResult] = useState({ tarif_total: 0, option_label: 'À définir', zone_label: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [associes, setAssocies] = useState([]);
  const [showCalendar, setShowCalendar] = useState(true);
  const [showFaq, setShowFaq] = useState(false);
  const { addItem } = useCart();

  // ── État plage de dates ─────────────────────────────────────────────────
  const [selectedDateStart, setSelectedDateStart] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);
  const calendarRef = useRef(null);
  const [selectedNbJours, setSelectedNbJours] = useState(1);
  const [rangeHasConflict, setRangeHasConflict] = useState(false);

  // ── Formule Photobooth ───────────────────────────────────────────────────
  const [photoboothFormula, setPhotoboothFormula] = useState(null);
  const isPhotobooth = product?.nom === 'Photobooth 1 en fer';

  const clientProfile = user?.profile || 'particulier';
  const profileCfg = PROFILE_DISCOUNTS[clientProfile] || PROFILE_DISCOUNTS.particulier;

  const [similaires, setSimilaires] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const selectedDateLocation = selectedDateStart;

  // ── Avis produit ──────────────────────────────────────────────────────────
  const [productReviews, setProductReviews] = useState({ reviews: [], total: 0, moyenne: 0 });

  useEffect(() => {
    if (!product?.id) return;
    fetch(`${BACKEND}/api/avis/product/${product.id}?limit=5`)
      .then(r => r.json())
      .then(d => setProductReviews(d))
      .catch(() => {});
  }, [product?.id]);

  useEffect(() => {
    // Cherche par UUID ou par slug SEO (location-xxx-figeac)
    const isSlug = id?.startsWith('location-');
    const endpoint = isSlug
      ? `${BACKEND}/api/products/by-slug/${encodeURIComponent(id)}`
      : `${BACKEND}/api/products/${id}`;

    fetch(endpoint)
      .then(r => r.json())
      .then(d => {
        setProduct(d);
        const baseP = d.tarifs?.[clientProfile] || d.tarifs?.particulier || 0;
        setSelectedPrice(baseP);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    fetch(`${BACKEND}/api/packs/upsell/${id}`)
      .then(r => r.json())
      .then(d => setSuggestions(d.suggestions || []))
      .catch((err) => { /* eslint-disable-line no-empty */ });

    fetch(`${BACKEND}/api/products/${id}/associes`)
      .then(r => r.json())
      .then(d => setAssocies(d.produits || []))
      .catch(() => {});

    // Phase 19 — Produits similaires + scénarios
    fetch(`${BACKEND}/api/produits-premium/${id}/similaires?limit=4`)
      .then(r => r.json())
      .then(d => setSimilaires(d.similaires || []))
      .catch(() => {});

    fetch(`${BACKEND}/api/produits-premium/${id}/scenarios`)
      .then(r => r.json())
      .then(d => setScenarios(d.scenarios || []))
      .catch(() => {});

    setSelectedImageIdx(0);
  // eslint-disable-next-line
  }, [id, clientProfile]);

  // ── Anti-doublon : vérification disponibilité lors de la sélection de dates ──
  const [availabilityChecking, setAvailabilityChecking] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState(null); // null | {disponible, message}

  async function checkAvailability(startDate, endDate, heureLiv = '09:00', heureRet = '10:00') {
    if (!product?.id || !startDate) return;
    setAvailabilityChecking(true);
    try {
      const res = await fetch(`${BACKEND}/api/booking/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_ids: [product.id],
          date_start: startDate,
          date_end: endDate || startDate,
          heure_livraison: heureLiv,
          heure_retour: heureRet,
        }),
      });
      const data = await res.json();
      setAvailabilityResult(data);
    } catch { /* silencieux */ }
    setAvailabilityChecking(false);
  }

  function handleDurationChange(durationKey, price) {
    setSelectedDuration(durationKey);
    setSelectedPrice(price);
  }

  // Prix public affiché (tarif particulier — visible de tous)
  const publicPrice = product?.tarifs?.particulier || 0;

  // Prix réel du client (selon profil si connecté)
  const clientProfilePrice = product?.tarifs?.[clientProfile] || publicPrice;

  // Prix base pour le PricingSelector (profil client)
  const basePrice = clientProfilePrice;

  // Totaux dynamiques
  const productTotal = selectedPrice * qty;
  const transportTotal = deliveryResult?.tarif_total || 0;
  const grandTotal = productTotal + transportTotal;
  const acompte = Math.round(grandTotal * 0.30 * 100) / 100;

  // Remise profil visible si connecté avec profil spécial
  const hasProfileDiscount = user && clientProfile !== 'particulier' && clientProfilePrice < publicPrice;
  const discountInfo = hasProfileDiscount
    ? getDiscountInfo(clientProfile, publicPrice, clientProfilePrice)
    : null;
  const savingsTotal = hasProfileDiscount
    ? Math.round((publicPrice - clientProfilePrice) * qty * (selectedPrice / (clientProfilePrice || 1)) * 100) / 100
    : 0;

  if (loading) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
        <div className="animate-spin w-10 h-10 rounded-full"
          style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} />
      </div>
    </div>
  );

  if (!product) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose text-center" style={{ paddingTop: '6rem' }}>
        <p style={{ color: 'var(--text-muted)' }}>Produit non trouvé</p>
        <Link to="/catalogue" className="btn-teal px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex mt-4">
          Retour catalogue
        </Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      {/* ── SEO dynamique par produit ── */}
      {(() => {
        const seo = getProductSEO(product);
        return (
          <SEO
            title={seo.title}
            description={seo.description}
            canonical={seo.canonical}
            image={seo.image}
            type="product"
            schema={seo.schema}
            breadcrumb={[
              { name: 'Catalogue', url: `${SITE}/catalogue` },
              { name: product.nom, url: seo.canonical },
            ]}
          />
        );
      })()}
      <Header />
      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '5rem' }}>
        <Link to="/catalogue"
          className="flex items-center gap-2 text-sm mb-6 hover:text-[var(--teal)]"
          style={{ color: 'var(--text-muted)' }}>
          <ArrowLeft size={16} /> Retour au catalogue
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* ── Image ── */}
          <div>
            <img
              data-testid="product-gallery"
              src={product.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80'}
              alt={`${product.nom} — location à Figeac, Lot (46) | Show Événement`}
              className="w-full rounded-2xl object-cover"
              style={{ maxHeight: '420px', border: '1px solid var(--border)' }}
              loading="eager"
              fetchpriority="high" />
            {/* Retour obligatoire */}
            <div className="flex items-center gap-2 p-3 rounded-xl mt-3"
              style={{ background: 'rgba(197,139,25,0.08)', border: '1px solid rgba(197,139,25,0.2)' }}>
              <Clock size={14} style={{ color: '#C58B19' }} />
              <p className="text-xs font-medium" style={{ color: '#C58B19' }}>
                Retour du matériel : le lendemain avant 10H00 maximum
              </p>
            </div>
          </div>

          {/* ── Infos produit ── */}
          <div>
            {/* Badges */}
            <div className="flex items-center gap-2 mb-3">
              {/* Labels produit — extrait pour éviter les ternaires imbriqués */}
              {(() => {
                const typeLabels = {
                  prestation: 'Prestation',
                  location: 'Location',
                  vente: 'Vente',
                  precommande: 'Précommande',
                  fabrication: 'Fabrication',
                };
                const typeLabel = typeLabels[product.type] || 'Autre';
                return (
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                    {typeLabel}
                  </span>
                );
              })()}
              {product.featured && (
                <span className="px-2 py-1 rounded-full text-xs"
                  style={{ background: 'rgba(212,175,55,0.12)', color: '#7A5E00' }}>
                  ★ Bestseller
                </span>
              )}
            </div>

            {/* Urgency */}
            <UrgencyBadge productId={product.id} productType={product.type} featured={product.featured} />

            <h1 className="font-display text-2xl sm:text-3xl font-bold mb-3 mt-3"
              style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              {product.nom}
            </h1>

            {product.type === 'prestation' && product.duree && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg mb-3" data-testid="prestation-duree"
                style={{ background: 'rgba(147,133,236,0.1)', border: '1px solid rgba(147,133,236,0.25)' }}>
                <Clock size={14} style={{ color: '#9385EC' }} />
                <span className="text-sm font-medium" style={{ color: '#6B5BC9' }}>Durée : {product.duree}</span>
              </div>
            )}

            <p className="text-sm mb-5" style={{ color: 'var(--text-muted)', lineHeight: '1.75' }}>
              {product.description_longue || product.description}
            </p>

            {/* ── PRIX / SUR DEVIS — différencié selon type ── */}
            {product.type === 'vente' || product.type === 'precommande' ? (
              /* ── VENTE : prix affiché ── */
              <div className="p-4 rounded-2xl mb-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>

                {/* Badge remise profil */}
                {hasProfileDiscount && discountInfo && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
                    style={{ background: discountInfo.bg, border: `1px solid ${discountInfo.color}30` }}>
                    <Percent size={13} style={{ color: discountInfo.color, flexShrink: 0 }} />
                    <div className="flex-1">
                      <p className="text-xs font-bold" style={{ color: discountInfo.color }}>
                        Tarif {discountInfo.label} : -{discountInfo.pct}% appliqué
                      </p>
                      <p className="text-xs" style={{ color: discountInfo.color, opacity: 0.8 }}>
                        Vous économisez <strong>{discountInfo.saving}€</strong> par rapport au tarif particulier
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-end justify-between mb-2">
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Prix</p>
                    {hasProfileDiscount ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm line-through" style={{ color: 'var(--text-muted)' }}>{publicPrice}€</span>
                        <span className="text-2xl font-bold price-gold">{clientProfilePrice}€</span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold price-gold">{publicPrice}€</span>
                    )}
                  </div>
                  {hasProfileDiscount && discountInfo && (
                    <div className="text-right">
                      <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Économie</p>
                      <p className="text-sm font-bold" style={{ color: discountInfo.color }}>-{discountInfo.saving}€</p>
                    </div>
                  )}
                </div>

                {!user && (
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    <Info size={11} className="inline mr-1" />
                    <Link to="/connexion" style={{ color: 'var(--teal)', textDecoration: 'underline' }}>Connectez-vous</Link>
                    {' '}pour votre tarif CE, association ou entreprise.
                  </p>
                )}
              </div>
            ) : (
              /* ── LOCATION : À partir de X€ ── */
              <div className="rounded-2xl mb-5 overflow-hidden"
                style={{ border: '1px solid rgba(93,188,191,0.3)' }}>
                {/* Badge remise profil — location */}
                {hasProfileDiscount && discountInfo && (
                  <div className="flex items-center gap-2 px-3 py-2"
                    style={{ background: discountInfo.bg, borderBottom: `1px solid ${discountInfo.color}20` }}>
                    <Percent size={13} style={{ color: discountInfo.color, flexShrink: 0 }} />
                    <p className="text-xs font-semibold" style={{ color: discountInfo.color }}>
                      Tarif {discountInfo.label} : -{discountInfo.pct}% automatiquement appliqué sur votre devis
                    </p>
                  </div>
                )}
                <div className="p-4" style={{ background: 'var(--teal-mist)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xs font-semibold mb-1" style={{ color: '#0B4B4E' }}>
                        Tarif location
                      </p>
                      <div className="flex items-baseline gap-1.5 flex-wrap mb-1">
                        {hasProfileDiscount ? (
                          <>
                            <span className="text-sm line-through" style={{ color: 'rgba(11,75,78,0.45)' }}>
                              {publicPrice}€
                            </span>
                            <span className="text-2xl font-bold price-gold">{clientProfilePrice}€</span>
                          </>
                        ) : (
                          <span className="text-2xl font-bold price-gold">
                            {publicPrice > 0 ? `À partir de ${publicPrice}€` : 'Nous contacter'}
                          </span>
                        )}
                        <span className="text-xs" style={{ color: 'rgba(11,75,78,0.6)' }}>/ journée</span>
                      </div>
                      <p className="text-xs" style={{ color: 'rgba(11,75,78,0.65)', lineHeight: 1.5 }}>
                        Prix ajusté selon la durée, la quantité et la zone.
                      </p>
                      {/* Badge livraison offerte */}
                      <div className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 rounded-lg"
                        style={{ background: 'rgba(31,157,122,0.07)', border: '1px solid rgba(31,157,122,0.2)', display: 'inline-flex' }}>
                        <Truck size={11} style={{ color: '#1F9D7A' }} />
                        <span className="text-xs font-semibold" style={{ color: '#1F9D7A' }}>
                          Livraison offerte jusqu'à 20 km de Figeac
                        </span>
                      </div>
                      {!user && (
                        <p className="text-xs mt-1.5" style={{ color: 'rgba(11,75,78,0.55)' }}>
                          <Info size={10} className="inline mr-1" />
                          <Link to="/connexion" style={{ color: 'var(--teal)', textDecoration: 'underline' }}>Connectez-vous</Link>
                          {' '}pour votre tarif CE, association ou entreprise.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── DURÉE (location uniquement, masqué car Sur devis) ── */}
            {false && product.type === 'location' && (
              <div className="p-4 rounded-2xl mb-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <PricingSelector
                  basePrice={basePrice}
                  profile={clientProfile}
                  onDurationChange={handleDurationChange}
                  initialDuration={selectedDuration}
                />
              </div>
            )}

            {/* ── INFORMATIONS TARIFAIRES (location uniquement, sauf Photobooth) ── */}
            {product.type === 'location' && !isPhotobooth && (
              <div className="mb-5">
                <BookingInfoBlock compact showTitle />
              </div>
            )}

            {/* ── SÉLECTEUR FORMULE PHOTOBOOTH ── */}
            {product.type === 'location' && isPhotobooth && (
              <div className="p-4 rounded-2xl mb-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <PhotoboothSelector onFormulaChange={setPhotoboothFormula} />
              </div>
            )}

            {/* ── DÉTAIL TARIFAIRE si dates sélectionnées (non Photobooth) ── */}
            {product.type === 'location' && !isPhotobooth && selectedDateStart && (
              <div className="mb-5">
                <PriceBreakdown
                  pricePerDay={clientProfilePrice}
                  nbDays={selectedNbJours || 1}
                  bookingType={daysToKey(selectedNbJours || 1)}
                  startDate={selectedDateStart}
                  endDate={selectedDateEnd}
                  qty={qty}
                  showAcompte
                />
              </div>
            )}

            {/* ── TRANSPORT ── */}
            {product.type === 'location' && (
              <div className="p-4 rounded-2xl mb-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <DeliverySelector
                  onDeliveryChange={setDeliveryResult}
                  initialOption="livraison_reprise"
                />
              </div>
            )}

            {/* ── ZONE CHECKER ── */}
            {product.type === 'location' && (
              <div className="p-4 rounded-2xl mb-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <ZoneChecker compact showSeoPhrase />
              </div>
            )}

            {/* ── RÉCAPITULATIF TOTAL — uniquement pour vente ── */}
            {(product.type === 'vente' || product.type === 'precommande') && (
              <div className="p-4 rounded-2xl mb-5"
                style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.25)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: '#0B4B4E' }}>
                  Récapitulatif
                </p>
                <div className="flex justify-between items-center py-1.5"
                  style={{ borderBottom: '1px dashed rgba(93,188,191,0.2)' }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#0B4B4E' }}>{product.nom} × {qty}</p>
                  </div>
                  <span className="font-bold" style={{ color: '#0B4B4E' }}>{productTotal.toFixed(2)}€</span>
                </div>
                {hasProfileDiscount && discountInfo && (
                  <div className="flex justify-between items-center py-1.5"
                    style={{ borderBottom: '1px dashed rgba(93,188,191,0.2)' }}>
                    <p className="text-sm font-medium flex items-center gap-1.5" style={{ color: discountInfo.color }}>
                      <Tag size={12} /> Remise {discountInfo.label} -{discountInfo.pct}%
                    </p>
                    <span className="font-bold text-sm" style={{ color: discountInfo.color }}>
                      -{(discountInfo.saving * qty).toFixed(2)}€
                    </span>
                  </div>
                )}
                <div className="mt-3 pt-3 flex justify-between items-center"
                  style={{ borderTop: '1px solid rgba(93,188,191,0.3)' }}>
                  <div>
                    <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>Total TTC</p>
                    <p className="text-xl font-bold price-gold">{productTotal.toFixed(2)}€</p>
                  </div>
                </div>
              </div>
            )}

            {/* ── QUANTITÉ + CTA — différencié location vs vente ── */}
            <div className="flex items-center gap-3 mb-5">
              {/* Contrôle quantité avec limite selon nb_unites */}
              {(() => {
                const isLocation = product.type === 'location';
                const maxQty = isLocation ? (product.nb_unites || 1) : 99;
                const atMax = qty >= maxQty;
                return (
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-2 px-1 rounded-xl"
                      style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}
                        className="w-9 h-9 flex items-center justify-center text-lg rounded-lg"
                        style={{ color: 'var(--text-muted)' }}>−</button>
                      <span className="font-bold w-8 text-center font-mono-ose"
                        style={{ color: 'var(--ink)' }}>{qty}</span>
                      <button
                        onClick={() => !atMax && setQty(q => q + 1)}
                        disabled={atMax}
                        title={atMax ? `${maxQty} unité${maxQty > 1 ? 's' : ''} disponible${maxQty > 1 ? 's' : ''}` : undefined}
                        className="w-9 h-9 flex items-center justify-center text-lg rounded-lg transition-opacity"
                        style={{
                          color: atMax ? 'var(--border)' : 'var(--ink)',
                          cursor: atMax ? 'not-allowed' : 'pointer',
                          opacity: atMax ? 0.35 : 1,
                        }}>+</button>
                    </div>
                    {isLocation && (
                      <span className="text-center"
                        style={{ fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.2 }}>
                        {maxQty === 1 ? '1 unité dispo' : `max ${maxQty}`}
                      </span>
                    )}
                  </div>
                );
              })()}
              {product.type === 'location' || product.type === 'fabrication' ? (
                /* ── LOCATION : bouton Ajouter à mon devis (date requise) ── */
                (() => {
                  const needsDate = product.type === 'location';
                  const dateMissing = needsDate && !selectedDateStart;
                  const unavailable = needsDate && availabilityResult && !availabilityResult.all_available;
                  return (
                    <button
                      data-testid="product-add-to-quote-button"
                      onClick={() => {
                        if (dateMissing) {
                          toast.error('Choisissez d\'abord vos dates de location pour éviter les doublons.');
                          setShowCalendar(true);
                          calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          return;
                        }
                        if (unavailable) {
                          toast.error('Ce produit est déjà réservé sur cette période. Choisissez une autre date.');
                          return;
                        }
                        const opts = {
                          ...(isPhotobooth && photoboothFormula ? { photobooth_formula: photoboothFormula } : {}),
                          ...(needsDate ? {
                            date_location: selectedDateStart,
                            date_fin: selectedDateEnd || selectedDateStart,
                            date_retour: selectedDateEnd || selectedDateStart,
                            nb_jours: selectedNbJours,
                            duree: daysToKey(selectedNbJours),
                            option_livraison: 'livraison_reprise',
                          } : {}),
                        };
                        addItem(product, qty, opts);
                        const label = isPhotobooth && photoboothFormula
                          ? ` — ${photoboothFormula.label} (${photoboothFormula.prix_base}€)`
                          : (needsDate ? ` · ${formatDate(selectedDateStart)}${selectedDateEnd && selectedDateEnd !== selectedDateStart ? ` → ${formatDate(selectedDateEnd)}` : ''}` : '');
                        toast.success(`Ajouté à votre devis${label}`);
                      }}
                      className="flex-1 btn-teal py-3 rounded-xl text-sm font-bold flex items-center gap-2 justify-center"
                      style={dateMissing ? { opacity: 0.6 } : undefined}>
                      <FileText size={16} />
                      {dateMissing
                        ? 'Choisir une date d\'abord'
                        : isPhotobooth && photoboothFormula
                        ? `Réserver · ${photoboothFormula.prix_base}€`
                        : 'Ajouter à mon devis'}
                    </button>
                  );
                })()
              ) : (
                /* ── VENTE : bouton Ajouter au panier ── */
                <button
                  data-testid="product-add-to-cart-button"
                  onClick={() => { addItem(product, qty); toast.success('Ajouté au panier'); }}
                  className="flex-1 btn-teal py-3 rounded-xl text-sm font-bold flex items-center gap-2 justify-center">
                  <ShoppingBag size={16} /> Ajouter au panier
                </button>
              )}
              <CallButton compact={false} label="Appeler" className="px-4 py-3" />
            </div>

            {/* Trust + Reviews */}
            <div className="mb-4"><TrustBlock compact /></div>
            {/* Avis clients sur ce produit */}
            {productReviews.total > 0 && (
              <div className="mb-4 p-4 rounded-2xl"
                style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Star size={15} style={{ color: '#D4AF37', fill: '#D4AF37' }} />
                  <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    {productReviews.moyenne}/5 ⭐ · {productReviews.total} avis client{productReviews.total > 1 ? 's' : ''}
                  </p>
                </div>
                {productReviews.reviews.slice(0, 3).map((avis, i) => (
                  <div key={i} className="mb-2 p-3 rounded-xl"
                    style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} size={11}
                            style={{ fill: n <= (avis.note||0) ? '#D4AF37' : 'none', color: n <= (avis.note||0) ? '#D4AF37' : '#D1D5DB' }} />
                        ))}
                      </div>
                      <span className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{avis.client_nom || 'Client'}</span>
                    </div>
                    {avis.commentaire && (
                      <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>{avis.commentaire}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="p-4 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
              <ReviewsWidget compact maxReviews={2} />
            </div>

            {/* ── CALENDRIER DISPONIBILITÉ — visible par défaut ── */}
            {product.type === 'location' && (
              <div className="mt-4" ref={calendarRef}>
                {/* Toggle titre */}
                <button onClick={() => setShowCalendar(!showCalendar)}
                  className="flex items-center gap-2 w-full p-3 rounded-xl text-sm font-medium mb-2"
                  style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.2)' }}>
                  <CalendarDays size={15} />
                  <span className="flex-1 text-left">
                    {selectedDateStart && selectedDateEnd
                      ? `📅 ${formatDate(selectedDateStart)} → ${formatDate(selectedDateEnd)} · ${selectedNbJours}j`
                      : selectedDateStart
                      ? `📅 Début : ${formatDate(selectedDateStart)} — sélectionnez une fin`
                      : 'Choisir vos dates de location'}
                  </span>
                  {showCalendar ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </button>

                {showCalendar && (
                  <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                    <AvailabilityCalendar
                      productId={product.id}
                      selectedStartDate={selectedDateStart}
                      selectedEndDate={selectedDateEnd}
                      onRangeSelect={(start, end, nb, conflict) => {
                        setSelectedDateStart(start);
                        setSelectedDateEnd(end);
                        setSelectedNbJours(nb || 1);
                        setRangeHasConflict(conflict);
                        // Vérifier l'anti-doublon en base
                        if (start) checkAvailability(start, end || start);
                        // Mettre à jour la durée dans PricingSelector si besoin
                        if (nb >= 1) {
                          const dk = daysToKey(nb);
                          const tarifs = product?.tarifs || {};
                          const base = tarifs[clientProfile] || tarifs.particulier || 0;
                          // Appliquer multiplicateur durée
                          const MULT = { '1j':1, '2j':1.8, '3j':2.5, '4j':3.2, '5j':3.8, 'semaine':5.1, '10j':8.5 };
                          setSelectedDuration(dk);
                          setSelectedPrice(Math.round(base * (MULT[dk] || 1) * 100) / 100);
                        }
                      }}
                      onDateSelect={() => {}}
                    />

                    {/* ── CTA RÉSERVER AVEC LA PLAGE ── */}
                    {selectedDateStart && (
                      <div className="p-4"
                        style={{ borderTop: '1px solid var(--border)', background: selectedDateEnd ? 'rgba(31,157,122,0.04)' : '#FAFAFA' }}>

                        {/* Récap plage */}
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: selectedDateEnd ? 'rgba(31,157,122,0.1)' : 'rgba(93,188,191,0.1)' }}>
                            <CalendarDays size={14} style={{ color: selectedDateEnd ? '#1F9D7A' : 'var(--teal)' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold mb-1"
                              style={{ color: selectedDateEnd ? '#1F9D7A' : '#0B4B4E' }}>
                              {selectedDateEnd
                                ? `${selectedNbJours} jour${selectedNbJours > 1 ? 's' : ''} sélectionné${selectedNbJours > 1 ? 's' : ''}`
                                : 'Date de début choisie'}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs px-2 py-0.5 rounded-lg font-semibold"
                                style={{ background: 'rgba(93,188,191,0.12)', color: '#0B4B4E' }}>
                                Début : {formatDate(selectedDateStart)}
                              </span>
                              {selectedDateEnd && (
                                <>
                                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
                                  <span className="text-xs px-2 py-0.5 rounded-lg font-semibold"
                                    style={{ background: 'rgba(31,157,122,0.1)', color: '#1F9D7A' }}>
                                    Fin : {formatDate(selectedDateEnd)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Estimation de prix multi-jours */}
                        {selectedDateEnd && selectedNbJours >= 1 && (
                          <div className="flex items-center justify-between p-3 rounded-xl mb-3"
                            style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
                            <div>
                              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                Estimation — {DURATION_LABELS[daysToKey(selectedNbJours)] || `${selectedNbJours}j`}
                              </p>
                              <p className="text-lg font-bold price-gold">
                                {selectedPrice > 0 ? `${(selectedPrice * qty).toFixed(0)}€` : 'Sur devis'}
                                <span className="text-xs font-normal ml-1" style={{ color: 'var(--text-muted)' }}>
                                  (hors livraison)
                                </span>
                              </p>
                            </div>
                            {hasProfileDiscount && discountInfo && (
                              <div className="text-right">
                                <p className="text-xs" style={{ color: discountInfo.color }}>Remise {discountInfo.label}</p>
                                <p className="text-sm font-bold" style={{ color: discountInfo.color }}>-{discountInfo.pct}%</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Alerte anti-doublon */}
                        {availabilityResult && !availabilityResult.all_available && (
                          <div className="flex items-start gap-2 mb-3 p-2.5 rounded-xl"
                            style={{ background: 'rgba(239,68,68,0.1)', border: '1.5px solid rgba(239,68,68,0.4)' }}>
                            <AlertCircle size={13} style={{ color: '#EF4444', flexShrink: 0, marginTop: 1 }} />
                            <div>
                              <p className="text-xs font-bold" style={{ color: '#EF4444' }}>
                                🚫 Ce produit est déjà réservé sur cette période.
                              </p>
                              <p className="text-xs mt-0.5" style={{ color: '#DC2626' }}>
                                Merci de choisir une autre date.
                              </p>
                            </div>
                          </div>
                        )}
                        {availabilityResult?.all_available && selectedDateStart && (
                          <div className="flex items-center gap-2 mb-3 p-2 rounded-xl"
                            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
                            <span style={{ color: '#22C55E', fontSize: 12 }}>✅</span>
                            <p className="text-xs font-semibold" style={{ color: '#16A34A' }}>
                              Disponible sur ce créneau !
                            </p>
                          </div>
                        )}

                        {/* Alerte délai < 7j */}
                        {(() => {
                          const diffDays = Math.floor((new Date(selectedDateStart) - new Date()) / (1000 * 60 * 60 * 24));
                          if (diffDays < 7) return (
                            <div className="flex items-start gap-2 mb-3 p-2.5 rounded-xl"
                              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                              <AlertCircle size={13} style={{ color: '#EF4444', flexShrink: 0, marginTop: 1 }} />
                              <p className="text-xs" style={{ color: '#DC2626', lineHeight: '1.5' }}>
                                ⚠️ Dans {diffDays}j — Paiement requis au moins <strong>7 jours avant</strong> l'événement.
                              </p>
                            </div>
                          );
                          return null;
                        })()}

                        {/* Alerte conflit */}
                        {rangeHasConflict && (
                          <div className="flex items-start gap-2 mb-3 p-2.5 rounded-xl"
                            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
                            <AlertCircle size={13} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 1 }} />
                            <p className="text-xs" style={{ color: '#92400E', lineHeight: '1.5' }}>
                              Certaines dates de cette plage sont en demande. Nous confirmerons la disponibilité après validation.
                            </p>
                          </div>
                        )}

                        {/* Bouton CTA */}
                        <button
                          data-testid="product-reserve-date-button"
                          disabled={availabilityResult && !availabilityResult.all_available}
                          onClick={() => {
                            if (availabilityResult && !availabilityResult.all_available) return;
                            addItem(product, qty, {
                              ...(isPhotobooth && photoboothFormula ? { photobooth_formula: photoboothFormula } : {}),
                              date_location: selectedDateStart,
                              date_fin: selectedDateEnd || selectedDateStart,
                              date_retour: selectedDateEnd || selectedDateStart,
                              nb_jours: selectedNbJours,
                              duree: daysToKey(selectedNbJours),
                              option_livraison: 'livraison_reprise',
                            });
                            const label = selectedDateEnd && selectedDateEnd !== selectedDateStart
                              ? `${formatDate(selectedDateStart)} → ${formatDate(selectedDateEnd)}`
                              : formatDate(selectedDateStart);
                            toast.success(`✅ ${product.nom} ajouté au devis`, {
                              description: `Dates : ${label} · ${selectedNbJours}j`,
                            });
                          }}
                          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm"
                          style={{
                            background: (availabilityResult && !availabilityResult.all_available)
                              ? '#9CA3AF' : '#1F9D7A',
                            color: '#FFFFFF', border: 'none',
                            cursor: (availabilityResult && !availabilityResult.all_available) ? 'not-allowed' : 'pointer',
                            boxShadow: '0 8px 24px rgba(31,157,122,0.3)',
                            opacity: availabilityChecking ? 0.7 : 1,
                          }}>
                          <Check size={16} />
                          {selectedDateEnd && selectedDateEnd !== selectedDateStart
                            ? `Réserver · ${formatDate(selectedDateStart)} → ${formatDate(selectedDateEnd)}`
                            : `Réserver · ${formatDate(selectedDateStart)}`}
                        </button>
                        <p className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>
                          Vous pouvez ajouter d'autres produits avant de finaliser · Acompte 30%
                        </p>
                      </div>
                    )}

                    {!selectedDateStart && (
                      <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>
                        👆 Cliquez sur une date <span style={{ color: '#22C55E', fontWeight: 600 }}>disponible</span> pour commencer
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* FAQ */}
            {product.faq?.length > 0 && (
              <div className="mt-4">
                <button onClick={() => setShowFaq(!showFaq)}
                  className="flex items-center gap-2 text-sm font-semibold mb-3"
                  style={{ color: 'var(--ink)' }}>
                  {showFaq ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  Questions fréquentes
                </button>
                {showFaq && product.faq.map((f, i) => (
                  <div key={f.question ? `faq-${f.question.slice(0, 20)}` : `faq-${i}`} className="mb-3">
                    <p className="text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>{f.question}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.65' }}>{f.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Produits à vendre associés */}
        {associes.length > 0 && (
          <div className="mt-10 pt-8" data-testid="associes-section" style={{ borderTop: '1px solid var(--border)' }}>
            <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
              À ajouter à votre {product.type === 'location' ? 'location' : 'prestation'}
            </h2>
            <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
              Des produits à acheter pour compléter votre événement
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {associes.map(a => (
                <div key={a.id} className="card-premium overflow-hidden group flex flex-col"
                  data-testid={`associe-card-${a.id}`}>
                  <Link to={`/produit/${a.id}`}>
                    <img src={a.images?.[0] || ''} alt={a.nom}
                      className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy" />
                  </Link>
                  <div className="p-3 flex-1 flex flex-col">
                    <p className="text-xs font-semibold line-clamp-2 mb-1" style={{ color: 'var(--ink)' }}>{a.nom}</p>
                    <p className="price-gold text-xs mb-2">{a.tarifs?.particulier || 0}€</p>
                    <button onClick={() => { addItem({ ...a, quantite: 1 }); toast.success(`${a.nom} ajouté`); }}
                      data-testid={`add-associe-${a.id}`}
                      className="mt-auto text-xs font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1"
                      style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                      <ShoppingBag size={12} /> Ajouter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="mt-10 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
            <h2 className="font-display text-xl font-bold mb-5" style={{ color: 'var(--ink)' }}>
              Indispensable avec ce produit
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {suggestions.slice(0, 10).map(s => (
                <Link key={s.id} to={`/produit/${s.id}`}
                  className="card-premium overflow-hidden group">
                  <img src={s.images?.[0] || ''} alt={s.nom}
                    className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy" />
                  <div className="p-3">
                    <p className="text-xs font-semibold line-clamp-2 mb-1" style={{ color: 'var(--ink)' }}>
                      {s.nom}
                    </p>
                    <p className="price-gold text-xs">à partir de {s.tarifs?.particulier || 0}€</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── SCÉNARIOS D'UTILISATION ── */}
        {scenarios.length > 0 && (
          <div className="mt-10 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
            <h2 className="font-display text-xl font-bold mb-5" style={{ color: 'var(--ink)' }}>
              💡 Scénarios d'utilisation
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {scenarios.map((sc, i) => (
                <div key={i} className="card-premium p-5">
                  <div className="text-3xl mb-3">{sc.emoji}</div>
                  <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>
                    {/* Retirer l'emoji du titre s'il est déjà affiché séparément */}
                    {sc.titre?.replace(/[\u{1F300}-\u{1FFFF}]|[\u{2700}-\u{27BF}]/gu, '').trim()}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{sc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PRODUITS SIMILAIRES ── */}
        {similaires.length > 0 && (
          <div className="mt-10 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="flex items-end justify-between mb-5">
              <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>
                Produits similaires
              </h2>
              <Link to="/catalogue" className="text-sm font-semibold flex items-center gap-1" style={{ color: 'var(--teal)' }}>
                Voir tout <ArrowLeft size={14} style={{ transform: 'rotate(180deg)' }} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {similaires.map(s => (
                <Link key={s.id} to={`/produit/${s.id}`} className="card-premium overflow-hidden group block">
                  {s.images?.[0] ? (
                    <img src={s.images[0]} alt={s.nom}
                      className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  ) : (
                    <div className="w-full h-36 flex items-center justify-center text-3xl" style={{ background: 'var(--bg)' }}>🎪</div>
                  )}
                  <div className="p-3">
                    <p className="text-xs font-semibold line-clamp-2 mb-1" style={{ color: 'var(--ink)' }}>{s.nom}</p>
                    <p className="price-gold text-xs">à partir de {s.tarifs?.particulier || 0}€</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <MobileStickyBar />
      <Footer />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} sourcePage={`Produit: ${product?.nom || ''}`} />
    </div>
  );
}
